import { Card } from '../../../molecules/Card/Card';
import type { ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, linearScale, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ComplaintsByDivisionView = {
  /** `Most complaints`. */
  kpis: KPIMetric[];
  /** One row per division with a complaint, most first. */
  rows: ChartRow[];
  xKey: string;
  /** The one `complaints` series. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
};

export type ComplaintsByDivisionCardProps = WidgetBlockProps & {
  view?: ComplaintsByDivisionView;
  /** Opens a division's complaints (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Intervals on the count axis — one more than the frame's five.
 *
 * `linearScale` rounds its step up to a `1`, `2` or `5`, and over five steps a
 * 52-complaint leader asks for 10.4 and is given 20, so the axis ends at 100 and
 * the longest bar fills half the plot. Six steps ask for 8.7, take 10, and the
 * axis ends at 60. The extra gridline is a smaller departure from the frame than
 * a chart drawn at half scale.
 */
const COUNT_AXIS_STEPS = 6;

/**
 * Community's complaints map (947:19575), and Overview's (972:19383).
 *
 * **Complaint counts, not a rate.** This card was built per million subscribers
 * — Dhaka files the most complaints because Dhaka has 18.4 million of them. The
 * user asked for the plain count on 2026-09-23 and that is what it draws now:
 * the trade is that the ranking is partly a population map, which the help text
 * says out loud rather than leaving a reader to infer.
 *
 * **One KPI, not the frame's two.** The frame leaves `Metric 2` visible carrying
 * the `KPI` component's own default pair (`Best performing` / `Positive`) with
 * no value rendered — the identical leftover that is *hidden* on five other
 * cards of the same frame. Confirmed with the user as a leftover, not a spec.
 */
export function ComplaintsByDivisionCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: ComplaintsByDivisionCardProps) {
  const hasData = view !== undefined && view.rows.length > 0;
  /*
   * A round axis, computed rather than left to Recharts — which fits its domain
   * to the longest bar and then divides it, drawing `0.0 / 0.9 / 1.9 / 2.9 /
   * 3.8 / 4.8` off this card's own data. See `linearScale`.
   */
  const valueKey = view?.series[0]?.key;
  const divisionScale = linearScale(
    view && valueKey ? view.rows.map((row) => Number(row[valueKey] ?? 0)) : [],
    COUNT_AXIS_STEPS,
  );
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No complaints in this period"
      {...cardState(status, hasData, onRetry)}
      title="Complaints by division"
      helpText="How many complaints came from each division, most first."
      helpSupportingText="Counts, not rates — a division with more subscribers will tend to sit higher, so Dhaka's position is partly a population map. Complaints here are the whole family — a billing issue, an outage report, a refund request and a cancellation are all somebody telling us something is wrong."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            onViewDetails={onViewDetails}
            xKey={view.xKey}
            series={view.series}
            orientation="horizontal"
            /* Division names are one short word; `Chattogram` is the longest. */
            categoryAxisWidth={80}
            height={PLOT_HEIGHT}
            legend="none"
            yDomain={divisionScale.domain}
            yTicks={divisionScale.ticks}
            /* Whole complaints, with a thousands separator once there are enough
               of them to need one. Nothing here is ever fractional. */
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
